import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TransactionReportingService } from 'app/core/services/transaction-reporting.service';
import { Subscription, Observable, forkJoin } from 'rxjs';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { isNullOrUndefined } from 'util';
import { WalletByTransaction } from 'app/core/models/walletByTransaction';
import { SupplierService } from 'app/core/services/supplier.service';
import { PosService } from 'app/core/services/pos.service';
import { mergeMap, map } from 'rxjs/operators';

@Component({
  selector: 'app-transaction-reports',
  templateUrl: './transaction-reports.component.html',
  styleUrls: ['./transaction-reports.component.css']
})
export class TransactionReportsComponent implements OnInit {

  transactionReportForm: FormGroup;
  transactionReportTypeItems: Array<String> = Array<String>("TRANSACTIONS_PER_POS", "TRANSACTIONS_PER_SUPPLIER");

  transactionReportTypeSub: Subscription;
  transactionReportPeriodSub: Subscription;
  numberOfItemsSubscription: Subscription;
  subscription: Subscription;
  chartTypeSubscription: Subscription;

  isLoading: boolean;

  numberOfItems: number = 10;
  period: number = 3;
  chartType: String = "bar";
  periodItems: Array<number> = Array<number>(1, 3, 6, 12);
  numberOfItemsList: Array<number> = Array<number>(3, 5, 10, 20);

  transactionReportType: string = "TRANSACTIONS_PER_SUPPLIER";
  transactionsReport: Array<WalletByTransaction> = new Array<WalletByTransaction>();

  reportData: number[] = [];
  reportLabel: string[] = [];
  reportTitle: string;

  fromDate: Date;
  isPerSupplier: boolean;

  criteria = {
    count: this.numberOfItems,
    windowBegin: this.getFromDate(3),
    windowEnd: new Date()
  };

  displayedColumns = ['supplierName', 'transactionCount'];

  constructor(
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private tsxReportingService: TransactionReportingService,
    private notifService: NotifMessageService,
    private supplierService: SupplierService,
    private posService: PosService) { }

  ngOnInit() {
    this.transactionReportForm = this.formBuilder.group({
      transactionReportType: new FormControl(this.transactionReportType, Validators.required),
      period: new FormControl(this.period, Validators.required),
      numberOfItems: new FormControl(this.numberOfItems, Validators.required),
      chartType: new FormControl('bar')
    });

    this.reportTitle = this.translate.instant("TRANSACTIONS_PER_SUPPLIER");
    this.getNumberOfTxBySupplier();
    this.numberOfItemsSubscription = this.transactionReportForm.get('numberOfItems').valueChanges.subscribe(val => {
      this.numberOfItems = val;
      if (this.isFormValid()) {
        this.criteria.count = this.numberOfItems;
        if (this.transactionReportType == "TRANSACTIONS_PER_SUPPLIER") {
          this.getNumberOfTxBySupplier();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        }
      }
    });

    this.transactionReportTypeSub = this.transactionReportForm.get("transactionReportType").valueChanges.subscribe(val => {
      this.transactionReportType = val;
      if (this.isFormValid()) {
        if (!isNullOrUndefined(this.period)) {
          this.criteria.windowEnd = new Date();
          this.criteria.windowBegin = this.getFromDate(this.period);
        }
        if (val == "TRANSACTIONS_PER_SUPPLIER") {
          this.getNumberOfTxBySupplier();
        } else if (val == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        }
      }
    });

    this.transactionReportPeriodSub = this.transactionReportForm.get("period").valueChanges.subscribe(val => {
      this.period = val;
      if (this.isFormValid()) {
        this.criteria.windowEnd = new Date();
        this.criteria.windowBegin = this.getFromDate(val);
        if (isNullOrUndefined(this.transactionReportType)) {
          this.getNumberOfTxBySupplier();
        }
        if (this.transactionReportType == "TRANSACTIONS_PER_SUPPLIER") {
          this.getNumberOfTxBySupplier();
        } else if (this.transactionReportType == "TRANSACTIONS_PER_POS") {
          this.getNumberOfTxByPos();
        }
      }
    });
    this.chartTypeSubscription = this.transactionReportForm.get("chartType").valueChanges.subscribe(val => {
      this.chartType = val;
    });
  }

  isFormValid(): boolean {
    if (this.transactionReportForm.get("transactionReportType").valid && this.transactionReportForm.get("period").valid && this.transactionReportForm.get("numberOfItems").valid)
      return true;
    return false;
  }
  getFromDate(period: number): Date {
    this.fromDate = new Date();
    this.fromDate.setMonth(this.fromDate.getMonth() - period);
    return this.fromDate;
  }

  getNumberOfTxBySupplier(): void {
    this.isPerSupplier = true;
    this.isLoading = true;
    this.tsxReportingService.getNumberOfTxBySupplier(this.criteria).subscribe(
      items => {
        this.reportLabel = [];
        this.reportData = [];
        this.transactionsReport = items;
        this.supplierService.getSuppliersById(items.map(items => items.supplierId)).subscribe(
          suppliers => {
            const map: Map<string, string> = new Map(suppliers.map(obj => [obj.id.toString(), obj.name]));
            this.transactionsReport.forEach((row, index) => {
              row.supplierName = map.get(row.supplierId);
              this.reportLabel[index] = row.supplierName;
            });
            this.reportTitle = this.translate.instant("TRANSACTIONS_PER_SUPPLIER");
          }
        );
        this.reportData = items.map(items => items.transactionCount);
        this.isLoading = false;
        if (items.empty) {
          this.notifService.notifyInfo('EMPTY_RESULT');
        }
      });
  }

  getNumberOfTxByPos(): void {
    this.isPerSupplier = false;
    this.isLoading = true;
    this.reportTitle = this.translate.instant("TRANSACTIONS_PER_POS");
    let transactionsReportData: Array<WalletByTransaction> = new Array<WalletByTransaction>();
    this.getMergedData().subscribe(data => {
      data.sort((a, b) => a.posId.localeCompare(b.posId));
      data.forEach((mergedData, index) => {
        if (mergedData) {
          if (index > 0 && data[index].posId === data[index - 1].posId) {
            mergedData.transactionCount = data[index].transactionCount + data[index - 1].transactionCount;
            transactionsReportData.pop();
            transactionsReportData.push(mergedData);
          } else {
            transactionsReportData.push(mergedData);
          }
        }
      });
      this.transactionsReport = transactionsReportData;
      this.reportData = this.transactionsReport.map(items => items.transactionCount);
      this.reportLabel = this.transactionsReport.map(items => items.posName);
      this.isLoading = false;
    })
  }

  public getMergedData(): Observable<any> {
    let mergedData: Array<WalletByTransaction> = new Array<WalletByTransaction>();
    return this.tsxReportingService.getNumberOfTxByPos(this.criteria)
      .pipe(
        mergeMap((result: Array<WalletByTransaction>) => {
          let posList = result.map(item => this.posService.getPosByIdForReport(item.posId));
          return forkJoin(posList).pipe(
            map((numberOfTxByPosList) => {
               for(let i=1; i<numberOfTxByPosList.length; i++){
                  if (numberOfTxByPosList[i] == null){
                  numberOfTxByPosList.splice(i,1);
                  result.splice(i,1);
                  i--;
                  }
               }
              const posIdNameMap: Map<string, string> = new Map(numberOfTxByPosList.map(obj => [obj.id.toString(), obj.name]));
              result.forEach(walletByTransaction => {
                  walletByTransaction.posName = posIdNameMap.get(walletByTransaction.posId);
                  mergedData.push(walletByTransaction);
              })
              return mergedData;
            })
          )
        })
      );
  }

  date() {
    let frDateFormat = "dd MMM y";
    let usDateFormat = "MMM dd y";
    if (this.translate.currentLang == "fr_FR")
      return frDateFormat;
    else
      return usDateFormat;
  }

  locale() {
    if (this.translate.currentLang == "fr_FR")
      return "fr_FR";
    else
      return "en_US";
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.transactionReportTypeSub) {
      this.transactionReportTypeSub.unsubscribe();
    }
    if (this.transactionReportTypeSub) {
      this.transactionReportTypeSub.unsubscribe();
    }
    if (this.numberOfItemsSubscription) {
      this.numberOfItemsSubscription.unsubscribe();
    }
    if (this.chartTypeSubscription) {
      this.chartTypeSubscription.unsubscribe();
    }
  }

}
